/**
 * GraphLegend — single source of truth for what colours / shapes / borders /
 * prefixes mean on the Cytoscape canvas. Synced to the actual stylesheet
 * in GraphCanvas.tsx — when a node selector changes, this file changes.
 *
 * Opened from the header Legend button only (no always-visible strip). The
 * panel is canvas-relative, draggable from the header, and resizable from all
 * eight edges/corners. Closing unmounts it so reopen resets to the safe default.
 *
 * Keyboard shortcuts intentionally OMITTED — they have a dedicated modal
 * (KeyboardShortcutsOverlay, opened by `?` / `F1` or the Keyboard tray
 * button). The footer CTA opens that overlay via openKeyboardShortcuts().
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, HelpCircle, ChevronDown, ChevronRight, GripHorizontal, RotateCcw } from 'lucide-react';
import { workbenchSurface } from './workbench/workbenchStyles';
import { diagramPalette } from '../constants/diagramPalette';
import { openKeyboardShortcuts } from './workbench/KeyboardShortcutsOverlay';

interface GraphLegendProps {
    onClose: () => void;
}

type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface PanelRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

const LEGEND_DEFAULT: PanelRect = {
    top: 64,
    left: 16,
    width: 280,
    height: 420,
};

const MIN_WIDTH = 220;
const MIN_HEIGHT = 200;

const RESIZE_HANDLES: Array<{ edge: ResizeEdge; className: string; cursor: string }> = [
    { edge: 'n', className: 'top-0 left-2 right-2 h-1.5 -translate-y-1/2', cursor: 'ns-resize' },
    { edge: 's', className: 'bottom-0 left-2 right-2 h-1.5 translate-y-1/2', cursor: 'ns-resize' },
    { edge: 'e', className: 'right-0 top-2 bottom-2 w-1.5 translate-x-1/2', cursor: 'ew-resize' },
    { edge: 'w', className: 'left-0 top-2 bottom-2 w-1.5 -translate-x-1/2', cursor: 'ew-resize' },
    { edge: 'ne', className: 'top-0 right-0 h-3 w-3 -translate-y-1/2 translate-x-1/2', cursor: 'nesw-resize' },
    { edge: 'nw', className: 'top-0 left-0 h-3 w-3 -translate-y-1/2 -translate-x-1/2', cursor: 'nwse-resize' },
    { edge: 'se', className: 'bottom-0 right-0 h-3 w-3 translate-y-1/2 translate-x-1/2', cursor: 'nwse-resize' },
    { edge: 'sw', className: 'bottom-0 left-0 h-3 w-3 translate-y-1/2 -translate-x-1/2', cursor: 'nesw-resize' },
];

function parentBounds(el: HTMLElement | null): { width: number; height: number } {
    const parent = el?.offsetParent as HTMLElement | null;
    if (!parent) {
        return {
            width: typeof window !== 'undefined' ? window.innerWidth : 1200,
            height: typeof window !== 'undefined' ? window.innerHeight : 800,
        };
    }
    return { width: parent.clientWidth, height: parent.clientHeight };
}

function clampRect(rect: PanelRect, bounds: { width: number; height: number }): PanelRect {
    const width = Math.max(MIN_WIDTH, Math.min(rect.width, Math.max(MIN_WIDTH, bounds.width - 8)));
    const height = Math.max(MIN_HEIGHT, Math.min(rect.height, Math.max(MIN_HEIGHT, bounds.height - 8)));
    const maxLeft = Math.max(0, bounds.width - width);
    const maxTop = Math.max(0, bounds.height - height);
    return {
        width,
        height,
        left: Math.min(Math.max(0, rect.left), maxLeft),
        top: Math.min(Math.max(0, rect.top), maxTop),
    };
}

/** High-contrast legend glyphs — sized for recognition, not canvas miniatures. */
const ShapeSVG: React.FC<{ shape: string; fill: string; stroke?: string; strokeStyle?: 'solid' | 'dashed' | 'double'; size?: number }> = ({ shape, fill, stroke, strokeStyle = 'solid', size = 20 }) => {
    const s = size;
    const cx = s / 2;
    const cy = s / 2;
    const pad = 2;
    const r = s / 2 - pad;
    const dash = strokeStyle === 'dashed' ? '3,2.5' : undefined;
    const sw = strokeStyle === 'double' ? 2.6 : 2.2;

    switch (shape) {
        case 'diamond':
            return <svg width={s} height={s} aria-hidden><polygon points={`${cx},${pad} ${s - pad},${cy} ${cx},${s - pad} ${pad},${cy}`} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} strokeLinejoin="round" /></svg>;
        case 'round-diamond':
            return <svg width={s} height={s} aria-hidden><polygon points={`${cx},${pad} ${s - pad},${cy} ${cx},${s - pad} ${pad},${cy}`} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray="3,2.5" strokeLinejoin="round" /></svg>;
        case 'triangle':
            return <svg width={s} height={s} aria-hidden><polygon points={`${cx},${pad} ${s - pad},${s - pad} ${pad},${s - pad}`} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} strokeLinejoin="round" /></svg>;
        case 'star': {
            const pts = Array.from({ length: 10 }, (_, i) => {
                const a = (Math.PI / 5) * i - Math.PI / 2;
                const rd = i % 2 === 0 ? r : r * 0.45;
                return `${cx + rd * Math.cos(a)},${cy + rd * Math.sin(a)}`;
            }).join(' ');
            return <svg width={s} height={s} aria-hidden><polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} strokeLinejoin="round" /></svg>;
        }
        case 'round-octagon': {
            const pts = Array.from({ length: 8 }, (_, i) => {
                const a = (Math.PI / 4) * i - Math.PI / 8;
                return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
            }).join(' ');
            return <svg width={s} height={s} aria-hidden><polygon points={pts} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray="3,2.5" strokeLinejoin="round" /></svg>;
        }
        case 'round-rectangle':
            return <svg width={s} height={s} aria-hidden><rect x={pad} y={pad + 1} width={s - pad * 2} height={s - pad * 2 - 2} rx="3.5" fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} /></svg>;
        case 'tag':
            return (
                <svg width={s + 10} height={s} aria-hidden>
                    <rect x={pad} y={pad + 1} width={s + 6} height={s - pad * 2 - 2} rx="3" fill={fill} stroke={stroke} strokeWidth={sw} />
                    <text x={(s + 10) / 2} y={cy + 0.5} textAnchor="middle" dominantBaseline="middle" fill={stroke ?? '#a78bfa'} fontSize="8" fontWeight="700">UPI</text>
                </svg>
            );
        default:
            return <svg width={s} height={s} aria-hidden><circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={dash} /></svg>;
    }
};

const SwatchPlate: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => (
    <div
        className={
            'flex items-center justify-center h-8 rounded-md bg-white dark:bg-slate-900 border border-slate-300/80 dark:border-slate-600 shadow-sm '
            + (wide ? 'w-11 px-0.5' : 'w-8')
        }
    >
        {children}
    </div>
);

const EdgeLine: React.FC<{ color: string; dash?: boolean; width: number; halo?: string }> = ({ color, dash, width, halo }) => (
    <svg width="36" height="14" aria-hidden>
        {halo && (
            <line x1="2" y1="7" x2="28" y2="7" stroke={halo} strokeWidth={width + 4} strokeOpacity={0.28} strokeLinecap="round" />
        )}
        <line x1="2" y1="7" x2="28" y2="7" stroke={color} strokeWidth={Math.max(width, 2)} strokeDasharray={dash ? '4,3' : 'none'} strokeLinecap="round" />
        <polygon points="26,3.5 33,7 26,10.5" fill={color} />
    </svg>
);

const SectionToggle: React.FC<{
    open: boolean;
    onToggle: () => void;
    label: string;
}> = ({ open, onToggle, label }) => (
    <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider hover:text-primary transition-colors w-full text-left"
    >
        {open ? <ChevronDown className="w-3 h-3 text-primary shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
        <span className="border-l-2 border-primary/70 pl-1.5">{label}</span>
    </button>
);

const LegendRow: React.FC<{ children: React.ReactNode; label: string }> = ({ children, label }) => (
    <div className="flex items-center gap-3 min-h-[32px]">
        <div className="min-w-8 flex justify-center shrink-0">{children}</div>
        <span className="text-[12px] leading-snug text-foreground font-medium">{label}</span>
    </div>
);

export const GraphLegend: React.FC<GraphLegendProps> = ({ onClose }) => {
    const [showShapes, setShowShapes] = useState(true);
    const [showColours, setShowColours] = useState(false);
    const [showEdges, setShowEdges] = useState(true);
    const [showMarkers, setShowMarkers] = useState(false);
    const [rect, setRect] = useState<PanelRect>({ ...LEGEND_DEFAULT });

    const panelRef = useRef<HTMLDivElement>(null);
    const interaction = useRef<{
        mode: 'drag' | 'resize';
        edge?: ResizeEdge;
        startX: number;
        startY: number;
        origin: PanelRect;
    } | null>(null);

    const applyClamped = useCallback((next: PanelRect) => {
        setRect(clampRect(next, parentBounds(panelRef.current)));
    }, []);

    const resetPosition = useCallback(() => {
        applyClamped({ ...LEGEND_DEFAULT });
    }, [applyClamped]);

    const applyClampedRef = useRef(applyClamped);
    useEffect(() => {
        applyClampedRef.current = applyClamped;
    }, [applyClamped]);

    const onPointerMove = useCallback((e: PointerEvent) => {
        const state = interaction.current;
        if (!state) return;
        const dx = e.clientX - state.startX;
        const dy = e.clientY - state.startY;
        const { origin } = state;
        const clamp = applyClampedRef.current;

        if (state.mode === 'drag') {
            clamp({
                ...origin,
                top: origin.top + dy,
                left: origin.left + dx,
            });
            return;
        }

        const edge = state.edge!;
        let { top, left, width, height } = origin;
        if (edge.includes('e')) width = origin.width + dx;
        if (edge.includes('w')) {
            width = origin.width - dx;
            left = origin.left + dx;
        }
        if (edge.includes('s')) height = origin.height + dy;
        if (edge.includes('n')) {
            height = origin.height - dy;
            top = origin.top + dy;
        }
        clamp({ top, left, width, height });
    }, []);

    const onPointerUpRef = useRef<(e: PointerEvent) => void>(() => undefined);

    const onPointerUp = useCallback((e: PointerEvent) => {
        if (!interaction.current) return;
        interaction.current = null;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUpRef.current);
        window.removeEventListener('pointercancel', onPointerUpRef.current);
        try {
            (e.target as HTMLElement | null)?.releasePointerCapture?.(e.pointerId);
        } catch {
            // Already released.
        }
    }, [onPointerMove]);

    useEffect(() => {
        onPointerUpRef.current = onPointerUp;
    }, [onPointerUp]);

    const beginInteraction = useCallback((
        e: React.PointerEvent,
        mode: 'drag' | 'resize',
        edge?: ResizeEdge,
    ) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        interaction.current = {
            mode,
            edge,
            startX: e.clientX,
            startY: e.clientY,
            origin: { ...rect },
        };
        document.body.style.userSelect = 'none';
        document.body.style.cursor = mode === 'drag'
            ? 'grabbing'
            : (RESIZE_HANDLES.find(h => h.edge === edge)?.cursor ?? 'default');
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
    }, [rect, onPointerMove, onPointerUp]);

    useEffect(() => () => {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }, [onPointerMove, onPointerUp]);

    useEffect(() => {
        const parent = panelRef.current?.offsetParent as HTMLElement | null;
        if (!parent || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => {
            setRect(prev => clampRect(prev, parentBounds(panelRef.current)));
        });
        ro.observe(parent);
        return () => ro.disconnect();
    }, []);

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-label="Graph Legend"
            data-tour="canvas-legend"
            data-testid="graph-legend"
            className={`${workbenchSurface} absolute z-40 flex flex-col overflow-hidden rounded-xl shadow-xl`}
            style={{
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
            }}
        >
            <div
                data-testid="graph-legend-drag-handle"
                onPointerDown={(e) => beginInteraction(e, 'drag')}
                className="px-3 py-2.5 flex items-center justify-between bg-brand-ink border-b-4 border-b-primary cursor-grab active:cursor-grabbing select-none shrink-0 touch-none"
            >
                <div className="flex items-center gap-1.5 min-w-0">
                    <GripHorizontal className="w-3.5 h-3.5 text-white/45 shrink-0" aria-hidden />
                    <HelpCircle className="w-3.5 h-3.5 text-primary shrink-0" />
                    <div className="min-w-0">
                        <span className="block font-semibold text-xs text-white leading-tight">Graph Legend</span>
                        <span className="block text-[10px] text-white/65 truncate">Drag to move · resize from edges</span>
                    </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                    <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={resetPosition}
                        className="p-1 rounded text-white/55 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Reset legend position"
                        title="Reset position"
                        data-testid="graph-legend-reset"
                    >
                        <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={onClose}
                        className="p-1 rounded text-white/55 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Close graph legend"
                        data-testid="graph-legend-close"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            <div className="p-3 space-y-3.5 flex-1 min-h-0 overflow-y-auto styled-scrollbar bg-card/40 dark:bg-card/60">
                <div>
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-l-2 border-primary/70 pl-1.5">
                        Risk on the canvas
                    </span>
                    <div className="mt-1.5 space-y-1.5 rounded-lg bg-brand-cream/35 dark:bg-primary/5 border border-primary/15 p-2">
                        <LegendRow label="Default — neutral border">
                            <SwatchPlate>
                                <div className="w-[18px] h-[14px] rounded-[3px] border-2 border-slate-500 bg-slate-100 dark:bg-slate-800" />
                            </SwatchPlate>
                        </LegendRow>
                        <LegendRow label="Critical — red border + red fill tint">
                            <SwatchPlate>
                                <div className="w-[18px] h-[14px] rounded-[3px] border-2 border-red-500 bg-red-100 dark:bg-red-950" />
                            </SwatchPlate>
                        </LegendRow>
                        <LegendRow label="Smart contract — dashed blue border line">
                            <SwatchPlate>
                                <div className="w-[18px] h-[14px] rounded-[3px] border-2 border-dashed border-blue-600 dark:border-blue-400 bg-sky-50 dark:bg-slate-800" />
                            </SwatchPlate>
                        </LegendRow>
                    </div>
                </div>

                <div>
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-l-2 border-primary/70 pl-1.5">
                        Trace role on the canvas
                    </span>
                    <div className="mt-1.5 space-y-1.5 rounded-lg border border-border/60 bg-muted/30 dark:bg-muted/15 p-2">
                        <LegendRow label="Teal border + ⚡ badge — Genesis funder / originating gas source">
                            <SwatchPlate>
                                <div
                                    className="w-[18px] h-[14px] rounded-[3px] border-[3px] border-teal-600 bg-teal-50 dark:bg-teal-950/40"
                                    data-testid="graph-legend-genesis-funder"
                                />
                            </SwatchPlate>
                        </LegendRow>
                        <LegendRow label="Double red border + ❄️ badge — Issuer Frozen (Tether/Circle blacklist)">
                            <SwatchPlate>
                                <div
                                    className="w-[18px] h-[14px] rounded-[3px] border-[3px] border-double border-red-600 bg-red-100 dark:bg-red-950/40"
                                    data-testid="graph-legend-issuer-frozen"
                                />
                            </SwatchPlate>
                        </LegendRow>
                        <LegendRow label="Dashed orange border + ❄️ badge — Issuer Freeze Target (Black USDT sink)">
                            <SwatchPlate>
                                <div
                                    className="w-[18px] h-[14px] rounded-[3px] border-[2.5px] border-dashed border-orange-600 bg-orange-50 dark:bg-orange-950/40"
                                    data-testid="graph-legend-freeze-target"
                                />
                            </SwatchPlate>
                        </LegendRow>
                        <LegendRow label="Green border — exchange or low-risk trace endpoint (not a safety guarantee)">
                            <SwatchPlate>
                                <div
                                    className="w-[18px] h-[14px] rounded-[3px] border-[3px] border-green-500 bg-green-50 dark:bg-green-950/40"
                                    data-testid="graph-legend-trace-endpoint"
                                />
                            </SwatchPlate>
                        </LegendRow>
                        <LegendRow label="Blue border — important intermediate wallet on the traced path">
                            <SwatchPlate>
                                <div
                                    className="w-[18px] h-[14px] rounded-[3px] border-[2.5px] border-blue-500 bg-blue-50 dark:bg-blue-950/40"
                                    data-testid="graph-legend-heavy-context"
                                />
                            </SwatchPlate>
                        </LegendRow>
                    </div>
                </div>

                <div>
                    <SectionToggle open={showShapes} onToggle={() => setShowShapes(!showShapes)} label="Variant Shapes" />
                    {showShapes && (
                        <div className="mt-1.5 space-y-1.5">
                            {[
                                { shape: 'round-rectangle', fill: '#e2e8f0', stroke: '#64748b', label: 'Address (default)' },
                                { shape: 'diamond', fill: '#fecaca', stroke: '#ef4444', label: 'Mixer (diamond)' },
                                { shape: 'diamond', fill: diagramPalette.court.fill, stroke: diagramPalette.court.stroke, label: 'Court / Legal hold (diamond)' },
                                { shape: 'triangle', fill: diagramPalette.mule.fill, stroke: diagramPalette.mule.stroke, label: 'Mule (triangle)' },
                                { shape: 'round-rectangle', fill: diagramPalette.cex.fill, stroke: diagramPalette.cex.stroke, label: 'Exchange (orange, not email blue)' },
                                { shape: 'round-rectangle', fill: diagramPalette.cex_deposit_verified.fill, stroke: diagramPalette.cex_deposit_verified.stroke, label: 'Confirmed exchange deposit' },
                                { shape: 'round-rectangle', fill: diagramPalette.victim.fill, stroke: diagramPalette.victim.stroke, label: 'Victim' },
                                { shape: 'round-rectangle', fill: diagramPalette.card.fill, stroke: diagramPalette.card.stroke, label: 'Card off-ramp' },
                                { shape: 'round-rectangle', fill: diagramPalette.identifier_email.fill, stroke: diagramPalette.identifier_email.stroke, label: 'Email identifier (blue)' },
                                { shape: 'triangle', fill: '#fecaca', stroke: '#ef4444', label: 'Scam (triangle)' },
                                { shape: 'star', fill: '#fecaca', stroke: '#dc2626', label: 'Hacker (star)' },
                                { shape: 'round-octagon', fill: '#fbcfe8', stroke: '#ec4899', strokeStyle: 'dashed' as const, label: 'Attribution Hub (pink octagon)' },
                                { shape: 'round-diamond', fill: '#e2e8f0', stroke: '#64748b', strokeStyle: 'dashed' as const, label: 'Ghost (round-diamond)' },
                                { shape: 'tag', fill: '#ede9fe', stroke: '#7c3aed', label: 'UPI ID (tag)' },
                            ].map(item => (
                                <LegendRow key={item.label} label={item.label}>
                                    <SwatchPlate>
                                        <ShapeSVG shape={item.shape} fill={item.fill} stroke={item.stroke} strokeStyle={item.strokeStyle} />
                                    </SwatchPlate>
                                </LegendRow>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <SectionToggle open={showColours} onToggle={() => setShowColours(!showColours)} label="Entity details (Inspector chips)" />
                    {showColours && (
                        <div className="mt-1.5 space-y-2 rounded-lg border border-border/60 bg-muted/40 dark:bg-muted/20 p-2">
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                                Category strips and subcategory borders were removed. Click a node — Entity Attribution and Subcategory chips in the Node Inspector carry that detail (hot wallet, deposit, bridge, OTC, Indian exchange, etc.).
                            </p>
                            <div className="flex flex-wrap gap-1">
                                {['Exchange', 'Bridge', 'DeFi', 'P2P', 'Hot Wallet', 'Deposit'].map(chip => (
                                    <span
                                        key={chip}
                                        className="px-1.5 py-0.5 bg-secondary text-foreground/85 rounded text-[9px] font-semibold border border-border"
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>
                            <LegendRow label="Purple double border = forensic subject (subpoena)">
                                <SwatchPlate>
                                    <div className="w-[18px] h-[14px] rounded-[3px] border-[2.5px] border-double border-purple-500 bg-purple-50 dark:bg-purple-950/40" />
                                </SwatchPlate>
                            </LegendRow>
                        </div>
                    )}
                </div>

                <div>
                    <SectionToggle open={showEdges} onToggle={() => setShowEdges(!showEdges)} label="Edge Types" />
                    {showEdges && (
                        <div className="mt-1.5 space-y-2.5">
                            <div>
                                <span className="text-[9px] text-muted-foreground uppercase tracking-wide">Taxonomy (stroke colour)</span>
                                <div className="mt-1 space-y-1.5">
                                    {[
                                        { color: '#64748b', dash: false, width: 2.5, label: 'Money flow (95% default)' },
                                        { color: '#3b82f6', dash: false, width: 2.5, label: 'Subpoena / proof anchor (Tier-1a)' },
                                        { color: '#ec4899', dash: true, width: 2.5, label: 'Inferred / off-chain link' },
                                        { color: '#8b5cf6', dash: false, width: 2.5, label: 'DEX swap' },
                                        { color: '#14b8a6', dash: false, width: 2.5, label: 'Stablecoin swap (same-chain)' },
                                        { color: '#f59e0b', dash: true, width: 2.5, label: 'Bridge hop (cross-chain)' },
                                        { color: '#a855f7', dash: true, width: 2.5, label: 'Mixer / CoinJoin / privacy pool' },
                                        { color: '#f59e0b', dash: false, width: 2.5, label: 'Traced path (Follow the Money)' },
                                    ].map(item => (
                                        <LegendRow key={item.label} label={item.label}>
                                            <SwatchPlate wide>
                                                <EdgeLine color={item.color} dash={item.dash} width={item.width} />
                                            </SwatchPlate>
                                        </LegendRow>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <span className="text-[9px] text-muted-foreground uppercase tracking-wide">Width tier (magnitude)</span>
                                <div className="mt-1 space-y-1.5">
                                    {[
                                        { width: 1.5, label: 'Thin: <$1k AND <5 txs' },
                                        { width: 2.5, label: 'Medium: $1k-$100k OR 5-50 txs' },
                                        { width: 4, label: 'Thick: >$100k OR >50 txs' },
                                    ].map(item => (
                                        <LegendRow key={item.label} label={item.label}>
                                            <SwatchPlate wide>
                                                <EdgeLine color="#64748b" dash={false} width={item.width} />
                                            </SwatchPlate>
                                        </LegendRow>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <span className="text-[9px] text-muted-foreground uppercase tracking-wide">Overlay</span>
                                <div className="mt-1 space-y-1.5">
                                    <LegendRow label="Gold overlay = on auto-trace path">
                                        <SwatchPlate wide>
                                            <EdgeLine color="#64748b" dash={false} width={2.5} halo="#f59e0b" />
                                        </SwatchPlate>
                                    </LegendRow>
                                    <LegendRow label="Blue overlay = hovered / selected">
                                        <SwatchPlate wide>
                                            <EdgeLine color="#64748b" dash={false} width={2.5} halo="#3b82f6" />
                                        </SwatchPlate>
                                    </LegendRow>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <SectionToggle open={showMarkers} onToggle={() => setShowMarkers(!showMarkers)} label="Markers & Prefixes" />
                    {showMarkers && (
                        <div className="mt-1.5 space-y-1.5">
                            <LegendRow label="Label prefix = Sanctioned (OFAC / EU / UN / UK / IL / Tether)">
                                <SwatchPlate>
                                    <span className="text-[16px] leading-none">!</span>
                                </SwatchPlate>
                            </LegendRow>
                            <LegendRow label="Label prefix = Smart contract">
                                <SwatchPlate>
                                    <span className="text-[16px] leading-none">{'{}'}</span>
                                </SwatchPlate>
                            </LegendRow>
                            <LegendRow label='Suffix "(Root Target)" = investigation root'>
                                <SwatchPlate>
                                    <span className="text-[11px] font-mono font-semibold text-foreground">(R)</span>
                                </SwatchPlate>
                            </LegendRow>
                            <LegendRow label='Suffix "(Destination)" = critical endpoint'>
                                <SwatchPlate>
                                    <span className="text-[11px] font-mono font-semibold text-foreground">(D)</span>
                                </SwatchPlate>
                            </LegendRow>
                            <LegendRow label="Amber border node = on auto-trace path">
                                <SwatchPlate>
                                    <div
                                        className="w-[18px] h-[14px] rounded-[3px] border-2 border-amber-500 bg-amber-50 dark:bg-amber-900/40"
                                        data-testid="graph-legend-gold-path-node"
                                    />
                                </SwatchPlate>
                            </LegendRow>
                            <LegendRow label="Double border = manually placed">
                                <SwatchPlate>
                                    <div className="w-[18px] h-[14px] rounded-[3px] border-[2.5px] border-double border-slate-500 bg-slate-100 dark:bg-slate-800" />
                                </SwatchPlate>
                            </LegendRow>
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    onClick={openKeyboardShortcuts}
                    className="pt-2 border-t border-border/60 text-[10px] text-muted-foreground text-center w-full hover:text-primary transition-colors"
                    title="Open keyboard shortcuts (? or F1)"
                    data-testid="graph-legend-shortcuts-cta"
                >
                    Press{' '}
                    <kbd className="px-1.5 py-0.5 bg-secondary border border-border rounded text-[10px] font-mono text-foreground">
                        ?
                    </kbd>{' '}
                    for keyboard shortcuts
                </button>
            </div>

            {RESIZE_HANDLES.map(({ edge, className, cursor }) => (
                <div
                    key={edge}
                    data-testid={`graph-legend-resize-${edge}`}
                    aria-hidden
                    onPointerDown={(e) => beginInteraction(e, 'resize', edge)}
                    className={`absolute z-50 touch-none ${className}`}
                    style={{ cursor }}
                />
            ))}
        </div>
    );
};
